import { useContext, useMemo } from 'react';
import classNames from 'classnames';

import CrossIcon from 'assets/icons/cross.svg';
import { ProductContext } from '@providers/ProductProvider';
import { ConfigContext } from '@providers/ConfigProvider';
import { CartContext } from '@providers/CartProvider';
import DeliveryTime from '@templates/DeliveryTime';
import Typography from '@components/atoms/Typography';
import AddToCart from '@components/AddToCart';
import LeaseLinkBar from '@components/LeaseLinkBar';
import ShippingPicker from '@components/ShippingPicker';
import ProductAvailability from '@components/ProductAvailability';
import { FormattedMessage } from 'utils/intl';
import getMaxQuantity from 'utils/getMaxQuantity';
import getNameByType from 'utils/getNameByType';

import classes from './PurchaseBar.module.scss';
import messages from './PurchaseBar.messages';

const PurchaseBar = (): JSX.Element => {
  const { shopId } = useContext(ConfigContext);
  const { onAddToCart } = useContext(CartContext);
  const {
    id,
    priceRrp,
    grossPrice,
    fullGrossPrice,
    fullPrice,
    availability,
    names,
    shipsUntil,
    currentSupplier,
    selectedComplementaryProductsIds,
    isLeasingAvailable,
    isEol,
    isPrerelease,
    isForSale,
  } = useContext(ProductContext);

  const purchaseMap = useMemo(
    () => [
      {
        component: (
          <div className={classNames(classes.addToCart, classes.eolBox)} data-nosnippet>
            <div className={classes.eolText}>
              <CrossIcon className={classes.crossIcon} />
              <Typography variant="body1">
                <FormattedMessage {...messages.eol} />
              </Typography>
            </div>
          </div>
        ),
        condition: isEol,
      },
      {
        component: (
          <>
            <div className={classes.addToCart} data-nosnippet>
              <AddToCart
                variantId={id}
                grossPrice={fullGrossPrice}
                netPrice={fullPrice}
                oldGrossPrice={priceRrp}
                stock={availability}
                productName={getNameByType(names, 'TITLE', shopId)}
                inShipping={!availability}
                currentSupplier={currentSupplier}
                withComplementary={fullGrossPrice.amount > grossPrice.amount}
                addToCart={(quantity: number) => onAddToCart(id, quantity, selectedComplementaryProductsIds)}
              />
            </div>
            {!!isLeasingAvailable && <LeaseLinkBar id={id} type="inline" withCalculator />}
            <div className={classNames(classes.shippingInfo)} data-nosnippet>
              {shipsUntil && !!availability && (
                <DeliveryTime shipsUntil={shipsUntil} currentSupplier={currentSupplier} />
              )}
              <div className={classNames(classes.availability, { [classes.isAvailable]: !!availability })}>
                <ProductAvailability
                  variant="picker"
                  quantity={getMaxQuantity(availability)}
                  unavailable={!availability}
                />
              </div>
              <ShippingPicker variant="freeShipping" />
              <ShippingPicker variant="guarantee" />
            </div>
          </>
        ),
        condition: isForSale && !isPrerelease,
      },
      {
        component: (
          <>
            <div className={classes.addToCart} data-nosnippet>
              <AddToCart
                variantId={id}
                grossPrice={grossPrice}
                productName={getNameByType(names, 'TITLE', shopId)}
                isPrerelease
              />
            </div>
            <div className={classNames(classes.shippingInfo)} data-nosnippet>
              <ProductAvailability variant="picker" isPrerelease />
              <ShippingPicker variant="freeShipping" />
              <ShippingPicker variant="guarantee" />
            </div>
          </>
        ),
        condition: isPrerelease,
      },
      {
        component: (
          <div className={classNames(classes.addToCart, classes.notForSale)} data-nosnippet>
            <Typography variant="h3" renderAs="div">
              <FormattedMessage {...messages.notForSale} />
            </Typography>
          </div>
        ),
        condition: true,
      },
    ],
    [id, selectedComplementaryProductsIds, fullPrice] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return <>{purchaseMap.find(({ condition }) => condition)?.component}</>;
};

export default PurchaseBar;
